import { ExportSite } from "constants/exportSites";

interface AdvancedDraftingShape {
  exportSites?: string[] | null;
  breed_id?: number | string | null;
  quantity?: number | null;
  draftingAttributes?: {
    advancedBreed1Id?: number | string | null;
    advancedBreed2Id?: number | string | null;
    advancedBreed3Id?: number | string | null;
    advancedBreed1Hd?: number | null;
    advancedBreed2Hd?: number | null;
    advancedBreed3Hd?: number | null;
  } | null;
}

export interface AdvancedDraftingErrors {
  advancedBreed: string;
}

/**
 * Validates the AuctionsPlus advanced-drafting rules: head-counts must sum to
 * the lot quantity, and the main breed must agree with breed1 (or be the
 * mixed-breed id when multiple breeds are present).
 *
 * Returns an errors object compatible with Formik's `setErrors`, or `null` if
 * validation passes / does not apply.
 */
export const validateAdvancedDrafting = (
  values: AdvancedDraftingShape,
  mixedBreedId: number | string | null | undefined,
): AdvancedDraftingErrors | null => {
  if (!values.exportSites?.includes(ExportSite.AUCTIONS_PLUS)) {
    return null;
  }

  const {
    advancedBreed1Id,
    advancedBreed2Id,
    advancedBreed3Id,
    advancedBreed1Hd,
    advancedBreed2Hd,
    advancedBreed3Hd,
  } = values.draftingAttributes ?? {};
  const { breed_id: breedId, quantity } = values;

  const breedCount = [
    advancedBreed1Hd,
    advancedBreed2Hd,
    advancedBreed3Hd,
  ].reduce<number>((acc, breed) => (breed ? acc + breed : acc), 0);

  if (breedCount !== quantity) {
    return {
      advancedBreed:
        "Advanced breed head counts have to be equal to sale lot quantity",
    };
  }
  if (!advancedBreed2Id && !advancedBreed3Id && advancedBreed1Id !== breedId) {
    return {
      advancedBreed: "Main breed has to be the same as advanced drafting breed",
    };
  }
  if ((advancedBreed2Id || advancedBreed3Id) && breedId !== mixedBreedId) {
    return {
      advancedBreed:
        "Main breed has to be set to mixed if different breeds are present",
    };
  }
  return null;
};
